<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <div class="panel panel-default panel-fill">
          <div class="panel-heading">
            <h3 class="panel-title">
              {{ trans("users.rolesHeadline") }}

              <info-popup :text-content="trans('tooltip.userRoles')" />
            </h3>
          </div>
          <div class="panel-body">
            <div class="row col-md-12">
              <div class="m-b-20 col-md-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': errors.userRoles }"
                >
                  <label for="role">{{ trans("users.userRoleLabel") }}</label>
                  <select2
                    id="role"
                    name="role"
                    :model-value="role"
                    @update:model-value="selectDropdownRole($event)"
                  >
                    <option value />
                    <option value="user">
                      {{ trans("roles.user") }}
                    </option>
                    <option value="consultant">
                      {{ trans("roles.consultant") }}
                    </option>
                    <option value="senior_consultant">
                      {{ trans("roles.senior_consultant") }}
                    </option>
                    <option value="wound_user">
                      {{ trans("roles.wound_user") }}
                    </option>
                    <option
                      v-if="$store.state.dermicusPlatform === 'stockholm'"
                      value="consultation_photographer"
                    >
                      {{ trans("roles.consultation_photographer") }}
                    </option>
                    <option value="user_without_clinical_view">
                      {{ trans("roles.user_without_clinical_view") }}
                    </option>
                  </select2>
                  <form-error
                    v-if="errors.userRoles"
                    :errors="errors.userRoles"
                  />
                </div>
              </div>
              <div v-if="$route.params.id !== 'new'" class="m-b-20 col-md-6">
                <DerToggleWithLabel
                  :model-value="user.blocked"
                  :label="trans('users.blockedUser')"
                  @update:model-value="user.blocked = $event"
                />
              </div>
              <div class="m-b-20 col-md-6 tw-space-y-2">
                <DerToggleWithLabel
                  :label="trans('roles.external_resource')"
                  :model-value="hasAnyExternalResourceRole"
                  @update:model-value="updateHasAnyExternalResourceRole"
                />

                <p class="mb-2">
                  <small>{{
                    trans("users.externalResourceRoleConsentAdmin")
                  }}</small>
                </p>

                <select2
                  v-if="hasAnyExternalResourceRole"
                  name="externalResourceRole"
                  :model-value="selectedExternalResourceRole"
                  disable-clear="true"
                  @update:model-value="updateSelectedExternalResourceRole"
                >
                  <option value="" />
                  <option value="external_resource">
                    {{ trans("roles.external_resource_full_title") }}
                  </option>
                  <option value="region_external_resource">
                    {{ trans("roles.region_external_resource_full_title") }}
                  </option>
                </select2>

                <table class="table table-hover table-actions-bar">
                  <tbody>
                    <tr
                      v-for="(externalResource, index) in externalResourcesList"
                      :key="index"
                    >
                      <td v-if="externalResource">
                        {{ externalResource.case_no }}
                      </td>
                    </tr>
                    <button
                      v-if="externalResources.length > 5"
                      class="btn btn-grey btn-lg btn-block mt-2"
                      @click="showAllExternalCases = !showAllExternalCases"
                    >
                      <i
                        :class="[
                          showAllExternalCases
                            ? 'fa-angle-up'
                            : 'fa-angle-down',
                          'fas',
                        ]"
                      />
                      {{
                        showAllExternalCases
                          ? trans("center.showLess")
                          : trans("examination.consultationShowAllButton")
                      }}
                    </button>
                  </tbody>
                </table>
              </div>
              <div class="m-b-20 col-md-6">
                <DerToggleWithLabel
                  v-model="userRoles.study_participator"
                  :label="trans('roles.study_participator')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <div class="panel panel-default panel-fill">
          <div class="panel-heading">
            <h3 class="panel-title">
              {{ trans("users.tabMenuLabelSettings") }}
              <info-popup :text-content="trans('tooltip.userSettings')" />
            </h3>
          </div>
          <div class="panel-body">
            <div class="row col-md-12">
              <div class="m-b-20 col-md-3">
                <div class="tw-flex tw-items-center tw-gap-x-2">
                  <DerToggleWithLabel
                    v-model="user.has_ai_prioritization"
                    :label="trans('admin.aiPrioritization')"
                    :disabled="!user.center_has_ai_prioritization"
                  />
                  <i
                    v-if="!user.center_has_ai_prioritization"
                    v-tooltip="
                      trans('aiFeatures.aiPrioritizationSettingDisabledInfo')
                    "
                    class="fas fa-question-circle"
                  />
                </div>
              </div>
              <div class="m-b-20 col-md-3">
                <div class="tw-flex tw-items-center tw-gap-x-2">
                  <DerToggleWithLabel
                    v-model="user.has_ai_diagnosis"
                    :label="trans('admin.aiDiagnosis')"
                    :disabled="!user.center_has_ai_diagnosis"
                  />
                  <i
                    v-if="!user.center_has_ai_diagnosis"
                    v-tooltip="
                      trans('aiFeatures.aiDiagnosisSettingDisabledInfo')
                    "
                    class="fas fa-question-circle"
                  />
                </div>
              </div>
              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="user.hide_case_answers"
                  :label="trans('users.hideConsultAnswerLabel')"
                />
              </div>
              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="user.hide_personal_data"
                  :label="trans('users.hidePersonalNoLabel')"
                />
              </div>
              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="userRoles.ai_tester"
                  :label="trans('users.toolsForImageAnalysis')"
                />
              </div>
              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="user.bypass_2fa"
                  :label="trans('users.bypass2faLabel')"
                />
                <i
                  v-tooltip="trans('users.bypass2faDescription')"
                  class="fas fa-question-circle"
                />
              </div>
            </div>

            <div class="row">
              <div class="m-b-20 col-md-12">
                <div
                  class="form-group"
                  :class="{ 'has-error': errors.culture }"
                >
                  <label for="culture">{{
                    trans("users.languageLabel")
                  }}</label>
                  <i class="far fa-asterisk" />
                  <select2 v-model="user.culture" name="culture">
                    <option
                      v-if="$store.state.dermicusPlatform === 'uk'"
                      value="en"
                    >
                      {{ trans("users.languageOptionEnglish") }}
                    </option>
                    <option value="sv">
                      {{ trans("users.languageOptionSwedish") }}
                    </option>
                    <option
                      v-if="$store.state.dermicusPlatform !== 'uk'"
                      value="en"
                    >
                      {{ trans("users.languageOptionEnglish") }}
                    </option>
                    <option value="nl">
                      {{ trans("users.languageOptionDutch") }}
                    </option>
                    <option value="de">
                      {{ trans("users.languageOptionGerman") }}
                    </option>
                    <option value="fr">
                      {{ trans("users.languageOptionFrench") }}
                    </option>
                    <option value="it">
                      {{ trans("users.languageOptionItalian") }}
                    </option>
                  </select2>

                  <form-error v-if="errors.culture" :errors="errors.culture" />
                </div>
              </div>
            </div>
            <span class="text-left"
              ><i class="far fa-asterisk" />
              {{ trans("center.requiredField") }}</span
            >
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <div class="panel panel-default panel-fill">
          <div class="panel-heading">
            <h3 class="panel-title">
              {{ trans("roles.adminAccess") }}
              <info-popup :text-content="trans('tooltip.userAdminRoles')" />
            </h3>
          </div>
          <div class="panel-body">
            <div class="row col-md-12">
              <div class="m-b-20 col-md-3" data-test="user-role-admin-toggle">
                <DerToggleWithLabel
                  v-model="userRoles.admin"
                  :disabled="!userRoles.admin || userRoles.super_admin === true"
                  :label="trans('roles.dermicusAdmin')"
                />
              </div>

              <div
                class="m-b-20 col-md-3"
                data-test="user-role-super-admin-toggle"
              >
                <DerToggleWithLabel
                  v-model="userRoles.super_admin"
                  :disabled="
                    !userRoles.super_admin || !$can('manage_super_settings')
                  "
                  :label="trans('roles.super_admin')"
                />
              </div>

              <div
                class="m-b-20 col-md-3"
                data-test="user-role-region-admin-toggle"
              >
                <DerToggleWithLabel
                  v-model="userRoles.region_admin"
                  :label="trans('roles.region_admin')"
                />
              </div>

              <div
                class="m-b-20 col-md-3"
                data-test="user-role-center-admin-toggle"
              >
                <DerToggleWithLabel
                  v-model="userRoles.center_admin"
                  :label="trans('roles.center_admin')"
                />
              </div>

              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="userRoles.case_admin"
                  :label="trans('roles.case_admin')"
                />
              </div>

              <div class="m-b-20 col-md-3">
                <DerToggleWithLabel
                  v-model="userRoles.study_admin"
                  :label="trans('roles.study_admin')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <AdminUserStudyAdmin :data-user="user" />
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <AdminUserUcrSettings
          :ucr-id="ucr.id"
          :user-id="user.id"
          @ucr-synced="$emit('ucr-synced')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import InfoPopup from "../../../Utils/InfoPopup.vue";
import AdminUserStudyAdmin from "../../../../app/admin/AdminUserStudyAdmin.vue";
import DerToggleWithLabel from "../../../../design-system/DerToggleWithLabel.vue";
import AdminUserUcrSettings from "./AdminUserUcrSettings.vue";
import UserRolesMixin from "../../../../mixins/UserRolesMixin.vue";

export default {
  components: {
    AdminUserStudyAdmin,
    AdminUserUcrSettings,
    InfoPopup,
    DerToggleWithLabel,
  },
  mixins: [UserRolesMixin],
  props: {
    user: {
      blocked: Boolean,
      has_wound_area: Boolean,
      has_ai_prioritization: Boolean,
      has_ai_diagnosis: Boolean,
      bypass_2fa: Boolean,
      center_has_ai_prioritization: Boolean,
      center_has_ai_diagnosis: Boolean,
      use_square_images: Boolean,
      user_type: Number,
      role: Number,
      hide_case_answers: Boolean,
      hide_personal_data: Boolean,
      culture: String,
    },
    externalResources: null,
    ucr: {
      active: Boolean,
      id: String,
    },
    errors: {},
    userRoles: {
      type: Object,
      default: () => ({}),
    },
    dropdownRole: String,
  },
  emits: ["ucr-synced"],
  data: function () {
    return {
      role: this.dropdownRole,
      showAllExternalCases: false,
    };
  },
  computed: {
    externalResourcesList: function () {
      if (
        !this.showAllExternalCases &&
        this.externalResources &&
        this.externalResources.length > 5
      ) {
        return this.externalResources.slice(0, 5);
      } else {
        return this.externalResources;
      }
    },
  },

  watch: {
    dropdownRole: function (newValue) {
      this.role = newValue;
    },
    "userRoles.admin": function (newValue) {
      this.confirmAdminLevelRemoval(
        "admin",
        "general.modalRemoveSuperadminMessage",
        newValue,
      );
    },
    "userRoles.super_admin": function (newValue) {
      this.confirmAdminLevelRemoval(
        "super_admin",
        "general.modalRemoveSuperDermicusAdminMessage",
        newValue,
      );
    },
  },

  methods: {
    confirmAdminLevelRemoval(role, messageKey, newValue) {
      if (newValue !== false) {
        return;
      }

      this.$vueAlert
        .confirm(this.trans(messageKey), undefined, undefined, {
          confirmButtonText: this.trans("general.modalConfirmButton"),
          cancelButtonText: this.trans("general.modalCancelButton"),
        })
        .then(() => {
          this.userRoles[role] = false;
        })
        .catch(() => {
          this.userRoles[role] = true;
        });
    },
  },
};
</script>
